import React, { useEffect, useState } from 'react';
import { CloudUploadOutlined, FileOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons'; // Thay PlusOutlined bằng InboxOutlined cho trực quan
import BaseModal from '@components/common/form/BaseModal';
import { APP_DETAILS_ACTION_DETAILS } from '@constants';
import apiConfig from '@constants/apiConfig';
import useBasicForm from '@hooks/useBasicForm';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, NumericField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { Button, Col, Flex, Form, Row, Spin, Switch, Upload } from 'antd';

const FORM_ID = 'app-version-form';

const AppVersionFormModal = ({ open, onClose, service, acceptedExtensions, onSuccess }) => {
    const translate = useTranslate();
    const [fileList, setFileList] = useState([]);
    const [computedThisVersion, setComputedThisVersion] = useState(null);
    const [detailThisVersion, setDetailThisVersion] = useState(null);
    const [detailVersion, setDetailVersion] = useState(null);
    const [apiMinVersion, setApiMinVersion] = useState(null);

    const { execute: executeUpload, loading: loadingUpload } = useFetch(apiConfig.file.uploadVersion);
    const { execute: executeGetDetails, loading: loadingDetails } = useFetch(apiConfig.file.appDetails);

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit: (values) => {
            if (fileList.length === 0) return;

            const formData = new FormData();
            formData.append('minVersion', values.minVersion);
            formData.append('thisVersion', values.thisVersion);
            formData.append('version', values.version);
            formData.append('requiredUpdate', values.requiredUpdate ? 'true' : 'false');
            formData.append('service', service);
            formData.append('file', fileList[0].originFileObj || fileList[0]);

            executeUpload({
                data: Object.fromEntries(formData.entries()),
                onCompleted: (res) => {
                    if (res?.result !== false) {
                        handleCancel();
                        onSuccess();
                    }
                },
                onError: () => { },
            });
        },
        setIsChangedFormValues: () => { },
    });

    useEffect(() => {
        if (open && service) {
            setFileList([]);
            form.resetFields();
            setComputedThisVersion(null);
            setDetailThisVersion(null);
            setDetailVersion(null);
            setApiMinVersion(null);

            executeGetDetails({
                params: { service, action: APP_DETAILS_ACTION_DETAILS },
                onCompleted: (res) => {
                    if (res?.result && res?.data) {
                        const data = res.data?.details;
                        const nextVersion = (data.this_version || 0) + 1;
                        const currentThisVer = data.this_version || 0;
                        const minVer = data.min_version || 0;
                        setComputedThisVersion(nextVersion);
                        setDetailThisVersion(currentThisVer);
                        setDetailVersion(data.version || '');
                        setApiMinVersion(minVer);
                        form.setFieldsValue({
                            thisVersion: nextVersion,
                            minVersion: minVer,
                            version: data.version || '',
                            requiredUpdate: data.required_update || false,
                        });
                    }
                },
                onError: () => { },
            });
        }
    }, [open, service]);

    const handleCancel = () => {
        if (loadingUpload) return;
        form.resetFields();
        setFileList([]);
        setComputedThisVersion(null);
        setDetailThisVersion(null);
        setDetailVersion(null);
        setApiMinVersion(null);
        onClose();
    };

    const handleUploadChange = ({ fileList: newFileList }) => {
        setFileList(newFileList.slice(-1));
    };

    const validateMinVersion = (_, value) => {
        if (value === undefined || value === null) {
            return Promise.resolve();
        }
        if (apiMinVersion !== null && value < apiMinVersion) {
            return Promise.reject(new Error(translate.formatMessage(commonMessage.minVersionLessThan)));
        }
        if (computedThisVersion !== null && value >= computedThisVersion) {
            return Promise.reject(new Error(translate.formatMessage(commonMessage.minVersionLessThan)));
        }
        return Promise.resolve();
    };

    const validateThisVersion = (_, value) => {
        if (value === undefined || value === null) {
            return Promise.resolve();
        }
        if (detailThisVersion !== null && value <= detailThisVersion) {
            return Promise.reject(new Error(translate.formatMessage(commonMessage.thisVersionGreaterThan)));
        }
        return Promise.resolve();
    };

    return (
        <BaseModal
            isModalVisible={open}
            handleCancel={handleCancel}
            title={translate.formatMessage(commonMessage.uploadNewVersion)}
            width="600px"
            noBodyPadding={false}
            customFooter={
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px' }}>
                    <Button danger onClick={handleCancel} disabled={loadingUpload} icon={<StopOutlined />}>{translate.formatMessage(commonMessage.cancel)}</Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form={FORM_ID}
                        loading={loadingUpload}
                        disabled={fileList.length === 0}
                        icon={<SaveOutlined />}
                    >
                        {translate.formatMessage(commonMessage.create)}
                    </Button>
                </div>
            }
        >
            <Spin spinning={loadingUpload || loadingDetails}>
                <BaseForm
                    id={FORM_ID}
                    form={form}
                    layout="vertical"
                    size='100%'
                    onValuesChange={onValuesChange}
                    onFinish={mixinFuncs.handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                requiredMark={false}
                                required
                                rules={[
                                    {
                                        validator: () => {
                                            if (fileList.length === 0) {
                                                return Promise.reject(
                                                    new Error(translate.formatMessage(commonMessage.selectFileFirst)),
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Upload.Dragger
                                    accept={acceptedExtensions}
                                    fileList={fileList}
                                    onChange={handleUploadChange}
                                    beforeUpload={() => false}
                                    maxCount={1}
                                    showUploadList={false}
                                    style={{ background: '#fafafa', padding: '10px 0' }}
                                >
                                    <Flex vertical align="center" justify="center" gap={8} style={{ minHeight: '100px', padding: '4px 0' }}>
                                        {fileList.length > 0 ? (
                                            <>
                                                <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
                                                    <FileOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                                                </p>
                                                <p style={{ color: '#262626', fontSize: 14, fontWeight: 500, margin: 0, padding: '0 12px', wordBreak: 'break-all' }}>
                                                    {fileList[0].name}
                                                </p>
                                                <p style={{ color: '#8c8c8c', fontSize: 12, margin: 0 }}>
                                                    {translate.formatMessage(commonMessage.upload)} ({translate.formatMessage(commonMessage.clickOrDragToReplace)})
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
                                                    <CloudUploadOutlined style={{ fontSize: 48, color: '#1677ff' }} />
                                                </p>
                                                <p style={{ color: '#262626', fontSize: 14, fontWeight: 500, margin: 0 }}>
                                                    {translate.formatMessage(commonMessage.dragOrClickToSelectFile)}
                                                </p>
                                                <p style={{ color: '#8c8c8c', fontSize: 12, margin: 0 }}>
                                                    {translate.formatMessage(commonMessage.supportedFormat)} {acceptedExtensions || translate.formatMessage(commonMessage.all)}
                                                </p>
                                            </>
                                        )}
                                    </Flex>
                                </Upload.Dragger>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <NumericField
                                required
                                label={`${translate.formatMessage(commonMessage.minVersion)}${detailThisVersion !== null ? ` (${apiMinVersion})` : ''}`}
                                name="minVersion"
                                rules={[
                                    { required: true, message: translate.formatMessage(commonMessage.required) },
                                    { validator: validateMinVersion },
                                ]}
                            />
                        </Col>
                        <Col span={24}>
                            <NumericField
                                label={`${translate.formatMessage(commonMessage.thisVersion)}${detailThisVersion !== null ? ` (${detailThisVersion})` : ''}`}
                                name="thisVersion"
                                rules={[
                                    { validator: validateThisVersion },
                                ]}
                            />
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <TextField
                                required
                                label={`${translate.formatMessage(commonMessage.version)}${detailVersion ? ` (${detailVersion})` : ''}`}
                                name="version"
                            />
                        </Col>
                        <Col span={24}>
                            <Flex align="center" gap={16} style={{ margin: '16px 0 24px' }}>
                                <Form.Item name="requiredUpdate" valuePropName="checked" noStyle>
                                    <Switch />
                                </Form.Item>
                                <span>{translate.formatMessage(commonMessage.requiredUpdate)}</span>
                            </Flex>
                        </Col>
                    </Row>
                </BaseForm>
            </Spin>
        </BaseModal>
    );
};

export default AppVersionFormModal;